import { Link } from '../../../../../widgets/buttons';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { PageHeader } from '../../../../../widgets/pageHeader';
import { SelectionType, SelectionTypeContainer } from '../../../../../widgets/selectionType';
import { showNotification } from '../../../../../widgets/notificationToaster';
import { useDialogManager } from '../../../../../widgets/dialog';
import { VerticalLayout } from '../../../../../widgets/layouts';
import React, { FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Section } from 'src/components/widgets/section';
import { NoIntegrationDialog } from 'src/shared/dialogs';
import { cartesianProduct, T } from 'src/shared/helpers';
import AddProductNoEcommerce from 'src/shared/icons/no-ecommerce.svg';
import AddProductFromZakeke from 'src/shared/icons/store.svg';
import styled from 'styled-components';
import { ContainerSmall } from '../../../containers';
import { addNewColorAction, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import ConnectModelDialog, { PartialSalesChannel } from '../../6-done/connectModelDialog';
import { menuSourceVariantsQuery } from './__generated__/menuSourceVariantsQuery.graphql';

interface MenuSourceVariantsProps {
    onChooseImport: () => void;
    onChooseScratch: () => void;
    productId: string;
}

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

const MenuSourceVariantsSection = styled(Section)`
    height: calc(100vh - 310px);
    > div {
        display: flex;
        align-items: center;
    }
`;

const MenuSourceVariantContainerSmall = styled(ContainerSmall)`
    margin: unset;
`;

const MenuSourceVariants: FC<MenuSourceVariantsProps> = ({ onChooseImport, onChooseScratch, productId }) => {
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const { salesChannels } = useLazyLoadQuery<menuSourceVariantsQuery>(
        graphql`
            query menuSourceVariantsQuery {
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                    }
                }
            }
        `,
        {}
    );

    const onImportVariants = () => {
        if (salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConnectModelDialog
                    additionalCodes={[]}
                    merchantProductOptionsConnected={[]}
                    productColors={[]}
                    productID={''}
                    forImportingVariants={true}
                    salesChannels={realSalesChannels as PartialSalesChannel[]}
                    productSalesChannelID={state.salesChannelID}
                    onConfirmImporting={(sourceMerchantProductOptions) => {
                        if (sourceMerchantProductOptions.filter((opt) => opt.optionID && opt.optionID != '').length > 0) {
                            let variantsImported = sourceMerchantProductOptions.map((attr) =>
                                attr.values.map((opt) => {
                                    return { Id: attr.optionID, Label: attr.label, Value: { Id: opt.valueID, Label: opt.label } };
                                })
                            );
                            let variantsToSave =
                                sourceMerchantProductOptions.length == 1 ? variantsImported[0].map((v) => [v]) : cartesianProduct(...variantsImported);
                            variantsToSave.forEach((variant: SelectedOptionValue[]) => {
                                dispatch(addNewColorAction(variant));
                            });

                            if (onChooseImport) onChooseImport();
                        } else {
                            showNotification({
                                type: 'danger',
                                message: <>{T._('Unable to import: product without variants.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        closeDialog('connectProductDialog');
                    }}
                />
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Unable to import variants', 'Admin')}
                    message={
                        <label>{T._('Complete the integration with Zakeke by selecting your ecommerce platform and connecting your store.', 'Admin')}</label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }
    };

    return (
        <VerticalLayout>
            <PageHeaderContainer>
                <PageHeader isBackVisible={false} title={T._('Choose how to set the product variations', 'Admin')}></PageHeader>
            </PageHeaderContainer>
            <MenuSourceVariantsSection>
                <MenuSourceVariantContainerSmall isFullWidth={true}>
                    <SelectionTypeContainer>
                        <SelectionType
                            key='scratch'
                            tip={
                                <>
                                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9HN48ZE1GXZ0HYQJNWWQGP'>
                                        {T._('Learn here how to do it.', 'Admin')}
                                    </Link>
                                </>
                            }
                            label={T._('Scratch', 'Admin')}
                            icon={<AddProductNoEcommerce />}
                            onClick={onChooseScratch}
                        />

                        <SelectionType
                            key='import'
                            tip={
                                <>
                                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693#h_01GH9HMQN3EK84CJGGBN16NDZ7'>
                                        {T._('Learn here how to do it.', 'Admin')}
                                    </Link>
                                </>
                            }
                            label={T._('Import', 'Admin')}
                            icon={<AddProductFromZakeke />}
                            onClick={onImportVariants}
                        ></SelectionType>
                    </SelectionTypeContainer>
                </MenuSourceVariantContainerSmall>
            </MenuSourceVariantsSection>
            <PageFooterButtons $fixed={true}></PageFooterButtons>
        </VerticalLayout>
    );
};

export default MenuSourceVariants;
